import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Gift, Sparkles } from 'lucide-react';

const COLORS = ['#68caf1', '#111827', '#C9A84C', '#374151'];

function formatPhoneInput(raw) {
  const digits = raw.replace(/\D/g, '').slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

export default function RaffleWheel() {
  const [data, setData] = useState(null); // { entries, winner, drawnAt, closeAt }
  const [loadError, setLoadError] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const [phone, setPhone] = useState('');
  const [lookupStatus, setLookupStatus] = useState('idle'); // idle | checking | done | error
  const [result, setResult] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await fetch('/api/raffle-enter');
      const json = await res.json();
      setData(json);
    } catch {
      setLoadError(true);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Segments sized by total entries (bonus referral entries = a bigger
  // slice), so the wheel itself communicates "refer friends for better
  // odds" at a glance.
  const segments = useMemo(() => {
    if (!data?.entries?.length) return [];
    const total = data.entries.reduce((sum, e) => sum + e.totalEntries, 0);
    let cursor = 0;
    return data.entries.map((e, i) => {
      const angle = (e.totalEntries / total) * 360;
      const seg = { ...e, startAngle: cursor, angle, color: COLORS[i % COLORS.length] };
      cursor += angle;
      return seg;
    });
  }, [data]);

  const gradient = useMemo(() => {
    if (!segments.length) return '#e5e7eb';
    const stops = segments.map(s => `${s.color} ${s.startAngle}deg ${s.startAngle + s.angle}deg`);
    return `conic-gradient(${stops.join(', ')})`;
  }, [segments]);

  useEffect(() => {
    if (!data?.winner || !segments.length || revealed) return;
    const winnerSeg = segments.find(s => s.id === data.winner.id);
    if (!winnerSeg) return;
    const center = winnerSeg.startAngle + winnerSeg.angle / 2;
    // Rotating the wheel clockwise by R moves the point that was at angle
    // `center` to land under the fixed top pointer once R = 360 - center.
    const landingRotation = 5 * 360 + ((360 - center) % 360);
    const t = setTimeout(() => {
      setRotation(landingRotation);
      setTimeout(() => setRevealed(true), 4200);
    }, 400);
    return () => clearTimeout(t);
  }, [data, segments, revealed]);

  async function checkResult(e) {
    e.preventDefault();
    const digits = phone.replace(/\D/g, '');
    if (digits.length !== 10) return;
    setLookupStatus('checking');
    try {
      const res = await fetch('/api/raffle-enter', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'check-result', phone: digits }),
      });
      const json = await res.json();
      setResult(json);
      setLookupStatus('done');
    } catch {
      setLookupStatus('error');
    }
  }

  const drawn = !!data?.winner;
  const bookNowUrl = '/detail/book?package=interior&promo=FB15';

  return (
    <div className="min-h-screen bg-[#F5F5F0] flex items-center justify-center px-4 py-16">
      <div className="max-w-md w-full text-center">
        <p className="text-[#68caf1] text-xs font-bold uppercase tracking-widest mb-3">Monarch Detailing</p>
        <h1 className="text-3xl font-bold text-[#111827] mb-3">
          {drawn ? 'The Raffle Has Been Drawn' : 'Entries Are Closed'}
        </h1>
        <p className="text-[#6B7280] text-base leading-relaxed mb-8">
          {drawn
            ? 'Watch the reveal below, then check your own result underneath.'
            : loadError ? 'The winner will be drawn live Friday, September 18 at 6:00 PM ET.'
            : data ? `${data.entries.length} entries so far. The winner is drawn live Friday, September 18 at 6:00 PM ET.`
            : 'Loading...'}
        </p>

        {segments.length > 0 && (
          <div className="relative w-64 h-64 mx-auto mb-4">
            <div
              className="absolute -top-1 left-1/2 -translate-x-1/2 z-10"
              style={{
                width: 0, height: 0,
                borderLeft: '10px solid transparent',
                borderRight: '10px solid transparent',
                borderTop: '16px solid #111827',
              }}
            />
            <div
              className="w-64 h-64 rounded-full border-4 border-white shadow-xl"
              style={{
                background: gradient,
                transform: `rotate(${rotation}deg)`,
                transition: rotation > 0 ? 'transform 4s cubic-bezier(0.17, 0.67, 0.16, 0.99)' : 'none',
              }}
            />
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="w-14 h-14 rounded-full bg-white shadow-md flex items-center justify-center">
                <Gift className="w-6 h-6 text-[#68caf1]" />
              </div>
            </div>
          </div>
        )}

        {drawn && revealed && (
          <div className="bg-[#111827] rounded-2xl p-6 mb-8">
            <Sparkles className="w-6 h-6 text-[#68caf1] mx-auto mb-2" />
            <p className="text-[#68caf1] text-xs font-bold uppercase tracking-widest mb-2">And The Winner Is</p>
            <p className="text-white text-2xl font-bold">{data.winner.firstName}!</p>
          </div>
        )}

        {/* Check your own result */}
        <div className="bg-white border border-gray-200 rounded-2xl p-6 text-left">
          <p className="text-xs font-bold text-[#111827] uppercase tracking-widest mb-3">Check Your Result</p>
          <form onSubmit={checkResult} className="flex gap-2 mb-1">
            <input
              type="tel"
              inputMode="numeric"
              value={phone}
              onChange={e => setPhone(formatPhoneInput(e.target.value))}
              placeholder="(301) 555-0100"
              required
              className="flex-1 px-4 py-3 border border-gray-200 rounded-xl text-sm text-[#111827] focus:outline-none focus:border-[#68caf1] transition-colors"
            />
            <button
              type="submit"
              disabled={lookupStatus === 'checking'}
              className="bg-[#68caf1] text-black font-bold px-5 rounded-xl hover:bg-[#5bb8db] transition-all active:scale-95 disabled:opacity-60"
            >
              {lookupStatus === 'checking' ? '...' : 'Check'}
            </button>
          </form>
          <p className="text-xs text-gray-400">The number you entered the raffle with.</p>

          {lookupStatus === 'done' && result && (
            <div className="mt-5 pt-5 border-t border-gray-100">
              {!result.found && (
                <div>
                  <p className="text-sm text-[#111827] mb-4">We couldn't find an entry with that number — but here's 15% off your first detail anyway, on us.</p>
                  <a href={bookNowUrl} className="block text-center w-full bg-[#68caf1] text-black font-bold py-3 rounded-2xl hover:bg-[#5bb8db] transition-all active:scale-95">Book Now — 15% Off</a>
                </div>
              )}
              {result.found && !result.drawn && (
                <p className="text-sm text-[#111827]">You're entered, {result.firstName}! The draw hasn't happened yet — check back Friday at 6:00 PM ET.</p>
              )}
              {result.found && result.drawn && result.isWinner && (
                <div>
                  <p className="text-lg font-bold text-[#111827] mb-1">🎉 You won, {result.firstName}!</p>
                  <p className="text-sm text-[#6B7280]">We'll be calling and emailing you at the number and address you entered with to schedule your free Alchemist Reset.</p>
                </div>
              )}
              {result.found && result.drawn && !result.isWinner && (
                <div>
                  <p className="text-sm text-[#111827] mb-4">Not this time, {result.firstName} — but thanks for entering. Here's 15% off your first detail anyway.</p>
                  <a href={bookNowUrl} className="block text-center w-full bg-[#68caf1] text-black font-bold py-3 rounded-2xl hover:bg-[#5bb8db] transition-all active:scale-95">Book Now — 15% Off</a>
                </div>
              )}
            </div>
          )}
        </div>

        <p className="text-xs text-center text-[#9CA3AF] mt-6">Monarch Detailing · Southern Maryland · (301) 885-9767</p>
      </div>
    </div>
  );
}
